import axios from 'axios';
/** 脱离 Axios 实现细节的统一请求错误。 */
export class ApiError extends Error {
    /** 错误所属类别。 */
    kind;
    /** HTTP 状态码，网络错误和取消请求时为空。 */
    status;
    /** 后端或 Axios 提供的错误码。 */
    code;
    /** 后端返回的非敏感附加信息。 */
    details;
    /** 创建带有分类、状态和附加信息的统一请求错误。 */
    constructor(options) {
        super(options.message);
        this.name = 'ApiError';
        this.kind = options.kind;
        this.status = options.status;
        this.code = options.code;
        this.details = options.details;
    }
}
/**
 * 已由调用方自行提示的错误集合。
 *
 * 用 WeakSet 而不是在 ApiError 上加字段，是为了同时覆盖响应校验失败抛出的
 * ZodError 等非 ApiError 错误；弱引用保证不会阻止这些对象被回收。
 */
const handledErrors = new WeakSet();
/**
 * 标记错误已由调用方自行提示，避免全局接口提示重复弹出同一次失败。
 *
 * 必须在 `await` 或 `catch` 里同步调用：全局提示延后一个宏任务才真正弹出，
 * 只要调用方在同一轮微任务内标记，全局提示就会跳过该错误。
 *
 * @param error 任意已捕获的错误对象。
 */
export function markApiErrorHandled(error) {
    if (typeof error === 'object' && error !== null)
        handledErrors.add(error);
}
/**
 * 判断错误是否已被调用方提示过。
 *
 * @param error 任意已捕获的错误对象。
 * @returns 已标记为已处理时返回 true。
 */
export function isApiErrorHandled(error) {
    return typeof error === 'object' && error !== null && handledErrors.has(error);
}
/** 将 Axios、取消请求和未知异常归一化为稳定的请求错误接口。 */
export function normalizeApiError(error) {
    if (error instanceof ApiError)
        return error;
    if (axios.isCancel(error)) {
        return new ApiError({ kind: 'cancelled', message: '请求已取消' });
    }
    if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const errorCode = typeof error.code === 'string' ? error.code : undefined;
        const kind = errorCode === 'ECONNABORTED' || errorCode === 'ETIMEDOUT'
            ? 'timeout'
            : status === undefined
                ? 'network'
                : 'http';
        return new ApiError({
            kind,
            message: getAxiosErrorMessage(error, kind),
            status,
            code: errorCode,
            details: error.response?.data,
        });
    }
    if (error instanceof Error) {
        return new ApiError({ kind: 'unknown', message: error.message });
    }
    return new ApiError({ kind: 'unknown', message: '请求失败' });
}
/** 根据 Axios 错误上下文生成不泄露请求内容的用户可读摘要。 */
function getAxiosErrorMessage(error, kind) {
    if (kind === 'timeout')
        return '请求超时，请稍后重试';
    if (kind === 'network')
        return '网络连接失败，请检查网络后重试';
    if (error.response?.status === 401)
        return '登录状态已失效';
    if (error.response?.status === 403)
        return '当前账号没有执行此操作的权限';
    if (error.response?.status === 404)
        return '请求的资源不存在';
    if (error.response?.status !== undefined && error.response.status >= 500)
        return '服务暂时不可用，请稍后重试';
    return '请求失败，请稍后重试';
}
/**
 * 判断错误是否为会话失效（401）。
 * 页面据此区分「请先登录」与通用加载失败，避免未登录时误导用户刷新重试。
 * @param error 查询或请求抛出的未归一化错误。
 * @returns 状态码为 401 时返回 true。
 */
export function isUnauthorizedError(error) {
    return normalizeApiError(error).status === 401;
}
/** 判断错误是否适合交给 Query 或业务模块重试。 */
export function isRetryableApiError(error) {
    const normalizedError = normalizeApiError(error);
    return (normalizedError.kind === 'network' ||
        normalizedError.kind === 'timeout' ||
        normalizedError.status === 408 ||
        normalizedError.status === 429 ||
        (normalizedError.status !== undefined && normalizedError.status >= 500));
}
